<template>
  <div class="all-projects">
    <CustomerEmptyProjectsListing v-if="!projects?.length" />
    
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
      <div v-for="project in projects" :key="project.id" class="project-card">
        <div class="bg-white rounded-lg shadow-sm overflow-hidden">
          <!-- Banner Image -->
          <div class="h-48 bg-gray-100 relative">
            <img 
              v-if="project.budget?.lineItems?.[0]?.attachments?.[0]" 
              :src="project.budget.lineItems[0].attachments[0]" 
              :alt="project.name"
              class="w-full h-full object-cover"
            />
            <div v-else class="w-full h-full flex items-center justify-center bg-gray-100">
              <span class="i-heroicons-photo text-4xl text-gray-400" />
            </div>
          </div>

          <!-- Project Details -->
          <div class="p-4">
            <h3 class="text-lg font-semibold mb-2">
              <router-link 
                :to="{ name: 'ShowProject', params: { customerId: project.customerId, projectId: project.id } }"
                class="text-gray-900 hover:text-primary-600"
              >
                {{ project.name }}
              </router-link>
            </h3>
            
            <p class="text-gray-600 text-sm mb-4 line-clamp-2">
              {{ project.description }}
            </p>

            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span 
                  class="px-2 py-1 text-xs rounded-full"
                  :class="{
                    'bg-yellow-100 text-yellow-800': project.status === PROJECT_STATUS.AWAITING_ACCEPTANCE,
                    'bg-purple-100 text-purple-800': project.status === PROJECT_STATUS.ESTIMATING,
                    'bg-green-100 text-green-800': project.status === PROJECT_STATUS.IN_PROGRESS,
                    'bg-red-100 text-red-800': project.status === PROJECT_STATUS.COMPLETED,
                    'bg-blue-100 text-blue-800': project.status === PROJECT_STATUS.ARCHIVED
                  }"
                >
                  {{ getProjectStatus(project.status) }}
                </span>
              </div>
              
              <div class="text-sm text-gray-500">
                {{ formatDate(project.createdAt) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CustomerEmptyProjectsListing from './CustomerEmptyProjectsListing.vue'
import { useQuery } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import { formatDate } from '@/utils'
import { PROJECT_STATUS } from '@/models/project'

const route = useRoute()
const customerId = computed(() => route.params.customerId)

const { data: projects } = useQuery({
  queryKey: ['projects', customerId.value],
  queryFn: async () => {
    const projects = await new ProjectService().find_by('customerId', '==', customerId.value)

    // sort by updatedAt descending
    projects.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())

    return projects
  }
})

const getProjectStatus = (status: string) => {
  switch (status) {
    case PROJECT_STATUS.AWAITING_ACCEPTANCE:
      return 'Awaiting Acceptance'
    case PROJECT_STATUS.ESTIMATING:
      return 'Estimating'
    case PROJECT_STATUS.IN_PROGRESS:
      return 'Work in Progress'
    case PROJECT_STATUS.COMPLETED:
      return 'Completed'
    case PROJECT_STATUS.ARCHIVED:
      return 'Archived'
    default:
      return status
  }
}
</script>

<style scoped>
.project-card {
  transition: transform 0.2s ease-in-out;
}

.project-card:hover {
  transform: translateY(-2px);
}
</style>
